import Page from "~/components/Page";
import Logo from "~/components/logo";
import Stepper from "~/components/Stepper";
import {
  Box,
  DarkMode,
  Flex,
  HStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { Mobile, Desktop } from "~/components/MediaQuery";
import MotionBox from "~/components/MotionBox";
import { FiUploadCloud, FiImage, FiMapPin } from "react-icons/fi";
import { AiOutlineShop } from "react-icons/ai";
import { useState } from "react";
import OnboardingGraph from "~/utils/OnboardingGraph";
import SideImage from "~/public/onboarding_side_image.jpg";
import Img from "react-optimized-image";

export default function OnboardingPage() {
  function Background() {
    return (
      <Flex minHeight="100vh" minWidth="100vw">
        {/* Left Panel */}
        <Box
          flex="2"
          backgroundColor={useColorModeValue(
            "light.background.strong",
            "dark.background.strong"
          )}
        />

        {/* Right Panel  */}
        <Desktop flex="1" position="relative">
          <Img
            style={{
              top: "0",
              left: "0",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "0% 0%",
            }}
            src={SideImage}
            webp
            sizes={[256, 450, 640]}
            densities={[1, 2]} // Retina support
            draggable="false"
            loading="lazy"
          />
          <Box
            position="absolute"
            top="0"
            left="0"
            width="100%"
            height="100%"
            backgroundColor="rgba(0,0,0,0.2)"
          />
        </Desktop>
      </Flex>
    );
  }

  function Foreground() {
    return (
      <Flex
        direction="column"
        justify="start"
        minHeight="100vh"
        width={{ mobile: "95%", desktop: "70%" }}
      >
        <Box pt="12" pb="6">
          <Logo />
        </Box>

        <Flex flex="1" alignItems="center" pb="6">
          <Content />
        </Flex>
      </Flex>
    );
  }

  return (
    <Page>
      <Box position="fixed">
        <Background />
      </Box>
      <Flex
        position="absolute"
        width="100vw"
        minHeight="100vh"
        justify="center"
        align="center"
      >
        <Foreground />
      </Flex>
    </Page>
  );
}

function Content() {
  const onboardingGraph = new OnboardingGraph();
  const [currentStep, setCurrentStep] = useState(onboardingGraph.firstStep());
  const [onboardingData, setOnboardingData] = useState({});

  const containerVariants = {
    bottom: { y: 100, opacity: 0 },
    center: { y: 0, opacity: 1 },
  };

  function updateOnboardingData(update) {
    setOnboardingData({ ...onboardingData, ...update });
  }

  function Step(props) {
    let stepVariants = {
      left: { x: -25, opacity: 0 },
      center: { x: 0, opacity: 1 },
    };

    return (
      <MotionBox
        flex="1"
        display="flex"
        initial="left"
        animate="center"
        variants={stepVariants}
      >
        {onboardingGraph.render(currentStep, props)}
      </MotionBox>
    );
  }

  function nextStep(preferredChild) {
    setCurrentStep(onboardingGraph.nextStep(currentStep, preferredChild));
  }

  function previousStep() {
    setCurrentStep(onboardingGraph.previousStep(currentStep));
  }

  return (
    <MotionBox
      flex="1"
      initial="bottom"
      animate="center"
      variants={containerVariants}
    >
      <Flex
        direction={{ mobile: "column", desktop: "row" }}
        backgroundColor={useColorModeValue(
          "light.background.superLite",
          "dark.background.superLite"
        )}
        borderRadius="brand"
        p="6"
        boxShadow="md"
        minHeight="65vh"
      >
        {!onboardingGraph.isLastStep(currentStep) && (
          <Flex
            pr={{ mobile: "0", desktop: "20" }}
            pb={{ mobile: "8", desktop: "0" }}
          >
            <Mobile flex="1">
              <Stepper
                direction="row"
                currentStep={onboardingGraph.stepNumber(currentStep)}
              >
                <AiOutlineShop />
                <FiUploadCloud />
                <FiImage />
                <FiMapPin />
              </Stepper>
            </Mobile>
            <Desktop flex="1">
              <Stepper
                direction="column"
                currentStep={onboardingGraph.stepNumber(currentStep)}
              >
                <AiOutlineShop />
                <FiUploadCloud />
                <FiImage />
                <FiMapPin />
              </Stepper>
            </Desktop>
          </Flex>
        )}
        <Flex flex="1">
          <Step
            onboardingData={onboardingData}
            showBack={onboardingGraph.stepNumber(currentStep) > 1}
            onComplete={(newOnboardingData, preferredChild) => {
              updateOnboardingData(newOnboardingData);
              nextStep(preferredChild);
            }}
            onBack={() => previousStep()}
            onSkip={(preferredChild) => {
              nextStep(preferredChild);
            }}
          />
        </Flex>
      </Flex>
    </MotionBox>
  );
}
